import { parseEmergencyProfile } from "../../utils/essentialFields";
import { ESSENTIAL_CATEGORY, ESSENTIAL_FIELDS, essentialFieldId, isPreferNotToShare } from "../../utils/fieldTypes";

/**
 * Safety status for the owner dashboard, based on the free/premium split:
 * the essential fields (required for everyone), a profile photo, and — for
 * Premium — at least one detail in the Premium sections.
 */
export function buildEssentialStatus({ profile, sections = [], isPremium = false }) {
  const essentialItems = sections.find((s) => s.category === ESSENTIAL_CATEGORY)?.items || [];
  const essentials = ESSENTIAL_FIELDS.map((field) => {
    const item = essentialItems.find((i) => essentialFieldId(i.key) === field.id);
    const value = item?.value?.trim() || "";
    const declined = isPreferNotToShare(value);
    return {
      id: field.id,
      label: field.label,
      value,
      filled: Boolean(value) || declined,
      declined,
      isVisible: !declined && item?.isVisible !== false,
    };
  });
  const filledCount = essentials.filter((e) => e.filled).length;
  const missing = essentials.filter((e) => !e.filled);

  const premiumSections = sections.filter((s) => s.category !== ESSENTIAL_CATEGORY);
  const premiumFilled = premiumSections.reduce(
    (n, s) => n + (s.items || []).filter((i) => i.value?.trim()).length,
    0
  );

  const extras = [{ id: "photo", label: "Add a Profile Photo", done: Boolean(profile?.photo) }];
  if (isPremium) extras.push({ id: "premium", label: "Add Medical Details", done: premiumFilled > 0 });

  const total = essentials.length + extras.length;
  const done = filledCount + extras.filter((e) => e.done).length;

  return {
    essentials,
    filledCount,
    missing,
    extras,
    premiumSections,
    percent: Math.round((done / total) * 100),
  };
}

export const LANG_OPTIONS = [
  { value: "english", label: "English" },
  { value: "hindi", label: "Hindi" },
  { value: "both", label: "English & Hindi" },
];

export function buildSafetyChecklist({ profile, sections = [], documentCount = 0, qrActive = false }) {
  const parsed = parseEmergencyProfile(sections);
  const blood = parsed.essentials.find((e) => e.id === "bloodGroup");
  const allergies = parsed.essentials.find((e) => e.id === "allergies");
  const hasContact = Boolean(parsed.primaryContact?.number);
  const hasId = (sections || []).some(
    (s) => s.category === "identification" && s.items?.some((i) => i.value?.trim())
  );

  const items = [
    { id: "name", label: "Name", done: Boolean(profile?.name?.trim()) },
    { id: "phone", label: "Phone", done: Boolean(profile?.phone?.trim()) },
    { id: "photo", label: "Profile photo", done: Boolean(profile?.photo) },
    { id: "blood", label: "Blood group", done: blood?.filled },
    { id: "contact", label: "Emergency contact", done: hasContact },
    { id: "allergies", label: "Allergies", done: allergies?.filled },
    { id: "documents", label: "Medical documents", done: documentCount > 0, premium: true },
    { id: "identification", label: "Identification", done: hasId },
    { id: "qr", label: "QR activated", done: qrActive },
  ];

  const done = items.filter((i) => i.done).length;
  const percent = Math.round((done / items.length) * 100);

  return { items, percent, parsed, blood, allergies };
}

export function formatProfileDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export function getPublicProfileUrl(qrId) {
  if (!qrId) return "";
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/${qrId}/public`;
}
